import type {
  DashboardPageConfig,
  DashboardPluginConfig,
  DashboardMenuPluginConfig,
  DashboardModalConfig,
  DashboardSidebarCategoryConfig,
  TopologyConfig,
  EmbeddedScriptConfig,
  CustomElementConfig,
  BlocksWidgetConfig,
  EmbeddedScriptParamsOverride,
  PlatformType,
  EcomAdditionalFeesConfig,
  EcomShippingRatesConfig,
  EcomDiscountsTriggerConfig,
  EcomValidationsConfig,
  EcomPaymentSettingsConfig,
  EcomGiftCardsProviderConfig,
  EcomRecommendationsProviderConfig,
  ExternalDatabaseProviderConfig,
  SeoKeywordsSuggestionConfig,
  PremiumCustomChargesConfig,
  MonitoringConfig,
  SitePluginConfig,
  BlocksWidgetPanelConfig,
  SiteComponentConfig,
  SiteComponentPanelConfig,
  SiteComponentElements,
  SiteComponentPresets,
  BookingsPricingProviderConfig,
  StaffSortingProviderConfig,
} from '@wix/cli-app-definitions';
import type { WixDependency } from '@wix/cli-app-manifest';
import type { TypeNames, VariantOf } from 'variant';
import { fields, variant } from 'variant';

interface PlatformTypes {
  platforms: PlatformType[];
}

interface DashboardPageComponent extends DashboardPageConfig, PlatformTypes {
  relativePath: string;
  route: string; // e.g. '/', '/foo/bar', '/foo%20bar'
}

interface SiteComponent extends SiteComponentConfig, PlatformTypes {
  relativePath: string;
  panels: Array<DevCenterComponent<'SiteComponentPanel'>>;
  elements: SiteComponentElements;
  presets?: SiteComponentPresets;
  relativeSdkPath?: string;
  relativePreviewPath?: string;
}

export interface SiteComponentWithPreview extends SiteComponent {
  relativePreviewPath: string;
}

export interface SiteComponentWithSdk extends SiteComponent {
  relativeSdkPath: string;
}

interface SiteComponentPanel extends SiteComponentPanelConfig, PlatformTypes {
  relativePath: string;
}

interface DashboardPluginComponent
  extends DashboardPluginConfig,
    PlatformTypes {
  relativePath: string;
}

interface DashboardMenuPluginComponent
  extends DashboardMenuPluginConfig,
    PlatformTypes {
  relativePath: string;
}

interface DashboardModalComponent extends DashboardModalConfig, PlatformTypes {
  relativePath: string;
  route: string;
}

interface EmbeddedScriptComponent extends EmbeddedScriptConfig, PlatformTypes {
  relativePath: string;
  paramsOverride: EmbeddedScriptParamsOverride | null;
  dependencies: WixDependency[];
}
interface CustomElementComponent extends CustomElementConfig, PlatformTypes {
  relativePath: string;
  relativePanelPath?: string;
  tagName: string;
  dependencies: WixDependency[];
}

interface SitePluginComponent extends SitePluginConfig, PlatformTypes {
  relativePath: string;
  relativePanelPath?: string;
  tagName: string;
  dependencies: WixDependency[];
}

interface BlocksWidgetPanelComponent
  extends BlocksWidgetPanelConfig,
    PlatformTypes {
  relativePath: string;
  widgetId: string;
  folderName: string;
}
interface BlocksWidgetComponent extends BlocksWidgetConfig, PlatformTypes {
  relativePath: string;
  apiPath: string;
}

interface BlocksMainComponent extends PlatformTypes {
  id: string;
}

interface DashboardSidebarCategory
  extends DashboardSidebarCategoryConfig,
    PlatformTypes {}

interface Topology extends TopologyConfig, PlatformTypes {}

type MonitoringComponent = MonitoringConfig &
  PlatformTypes & {
    id: string;
  };

interface ApiComponent extends PlatformTypes {
  title: string;
  relativePath: string;
}

interface WebMethod extends PlatformTypes {
  relativePath: string;
}

interface EventComponent extends PlatformTypes {
  title: string;
  relativePath: string;
}

interface ServicePluginBaseComponent extends PlatformTypes {
  relativePath: string;
  servicePlugin: VariantOf<typeof ServicePlugin>;
}

interface StaffSortingProvider extends StaffSortingProviderConfig {}

interface BookingsPricingProvider extends BookingsPricingProviderConfig {}

interface EcomShippingRates extends EcomShippingRatesConfig {}

interface EcomAdditionalFees extends EcomAdditionalFeesConfig {}

interface EcomDiscountsTrigger extends EcomDiscountsTriggerConfig {}

interface EcomValidations extends EcomValidationsConfig {}

interface EcomPaymentSettings extends EcomPaymentSettingsConfig {}

interface EcomGiftCardsProvider extends EcomGiftCardsProviderConfig {}

interface EcomRecommendationsProvider
  extends EcomRecommendationsProviderConfig {}

interface ExternalDatabaseProvider extends ExternalDatabaseProviderConfig {}

interface SeoKeywordsSuggestion extends SeoKeywordsSuggestionConfig {}

interface PremiumCustomCharges extends PremiumCustomChargesConfig {}

export const ServicePlugin = variant({
  StaffSortingProvider: fields<StaffSortingProvider>(),
  BookingsPricingProvider: fields<BookingsPricingProvider>(),
  EcomShippingRates: fields<EcomShippingRates>(),
  EcomAdditionalFees: fields<EcomAdditionalFees>(),
  EcomDiscountsTrigger: fields<EcomDiscountsTrigger>(),
  EcomValidations: fields<EcomValidations>(),
  EcomPaymentSettings: fields<EcomPaymentSettings>(),
  EcomGiftCardsProvider: fields<EcomGiftCardsProvider>(),
  EcomRecommendationsProvider: fields<EcomRecommendationsProvider>(),
  ExternalDatabaseProvider: fields<ExternalDatabaseProvider>(),
  SeoKeywordsSuggestion: fields<SeoKeywordsSuggestion>(),
  PremiumCustomCharges: fields<PremiumCustomCharges>(),
});

export type ServicePlugin = VariantOf<typeof ServicePlugin>;

export const DevCenterComponent = variant({
  DashboardPage: fields<DashboardPageComponent>(),
  DashboardPlugin: fields<DashboardPluginComponent>(),
  DashboardMenuPlugin: fields<DashboardMenuPluginComponent>(),
  DashboardModal: fields<DashboardModalComponent>(),
  DashboardSidebarCategory: fields<DashboardSidebarCategory>(),
  EmbeddedScript: fields<EmbeddedScriptComponent>(),
  CustomElement: fields<CustomElementComponent>(),
  SitePlugin: fields<SitePluginComponent>(),
  BlocksWidget: fields<BlocksWidgetComponent>(),
  BlocksWidgetPanel: fields<BlocksWidgetPanelComponent>(),
  BlocksMainComponent: fields<BlocksMainComponent>(),
  Topology: fields<Topology>(),
  Api: fields<ApiComponent>(),
  WebMethod: fields<WebMethod>(),
  Event: fields<EventComponent>(),
  ServicePlugin: fields<ServicePluginBaseComponent>(),
  Monitoring: fields<MonitoringComponent>(),
  SiteComponent: fields<SiteComponent>(),
  SiteComponentPanel: fields<SiteComponentPanel>(),
});

export type DevCenterComponent<
  T extends TypeNames<typeof DevCenterComponent> = undefined,
> = VariantOf<typeof DevCenterComponent, T>;

export type BackendComponent =
  | DevCenterComponent<'Api'>
  | DevCenterComponent<'WebMethod'>
  | DevCenterComponent<'Event'>
  | DevCenterComponent<'ServicePlugin'>;

export type VirtualComponent =
  | DevCenterComponent<'BlocksMainComponent'>
  | DevCenterComponent<'Topology'>
  | DevCenterComponent<'Monitoring'>;
